<template>
  <eg-b-modal
    :id="`export-to-${type}-modal`"
    :title="$t('essays.export.exportFeedbackTo', { type: type.toUpperCase() })"
    size="lg"
    centered
    @show="onShow"
    @hide="onHide"
  >
    <template #modal-footer="{ close }">
      <unreviewed-alert @close="close" />
      <div class="d-flex align-items-center justify-content-end">
        <eg-b-button variant="secondary" @click="close">
          {{ success ? $t("common.close") : $t("common.cancel") }}
        </eg-b-button>
        <eg-b-button
          v-if="!success"
          class="ml-2"
          :disabled="exporting || selected.length === 0"
          variant="info"
          @click="onExport"
        >
          {{ $t("common.export") }}
        </eg-b-button>
      </div>
    </template>

    <div>
      <div
        v-if="success"
        class="d-flex flex-column align-items-center justify-content-center w-100"
      >
        <eg-b-icon
          icon="check-circle-fill"
          variant="success"
          :style="{ width: '48px', height: '48px' }"
        />
        <h4 class="text-center mt-2">{{ $t("essays.exportComplete") }}</h4>

        <div v-if="popupBlocked" class="text text-center mb-3">
          {{ $t("essays.export.popupBlocked") }}
          <a :href="exportUrl" target="_blank" class="d-block mt-2">
            {{ $t("essays.export.clickToDownload") }}
          </a>
        </div>
        <div v-else class="text text-center mb-3">
          {{ $t("essays.export.downloadAutoStart") }}
          <a :href="exportUrl" target="_blank">{{ $t("essays.clickHere") }}</a
          >.
        </div>
      </div>
      <div v-else-if="exporting">
        <div
          class="d-flex flex-column align-items-center justify-content-center w-100"
          style="min-height: 200px"
        >
          <eg-b-progress
            v-if="exportProgress > 0"
            :value="exportProgress"
            :max="100"
            class="w-100 mb-3"
            height="4px"
          ></eg-b-progress>

          <eg-b-spinner
            class="my-3"
            style="width: 51px; height: 51px"
            variant="info"
          />

          <h4 class="text-center">
            {{ $t("essays.export.exportingTo", { type: type.toUpperCase() }) }}
          </h4>

          <p v-if="exportError" class="text-danger mt-2">
            {{ exportError }}
          </p>
        </div>
      </div>
      <div v-else>
        <div class="text mb-3">
          <span v-if="type === 'pdf' && multipleEssaysSelected">
            {{ $t("essays.export.essaysExportedAs") }}
            <b>{{ mergedPdf ? $t("essays.exportSinglePdf") : ".zip" }}</b>
            {{ $t("essays.export.file") }}.
          </span>
          {{ description }}
        </div>

        <div>
          <export-options
            v-model="selected"
            stacked
            :available-options="availableOptions"
          />
        </div>
      </div>
    </div>
  </eg-b-modal>
</template>

<script>
// import { saveAs } from "file-saver";
import { createPolling } from "@/utils/polling";
import { startPdfExport, checkPdfExportStatus } from "@/api/requests/pdfExport";
import { exportToPdf } from "@/api/requests/export";
import ExportOptions from "./export_options.vue";
import UnreviewedAlert from "./unreviewed_alert.vue";

export default {
  name: "ExportToPdfModal",

  components: {
    ExportOptions,
    UnreviewedAlert,
  },

  props: {
    selectedAll: {
      type: Boolean,
      default: false,
    },

    selectedIds: {
      type: Array,
      default: () => [],
    },

    filters: {
      type: Object,
      default: () => ({}),
    },

    mergedPdf: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      success: false,
      exporting: false,
      polling: null,
      selected: [],
      fileName: "",
      show: false,
      type: "pdf",
      exportProgress: 0,
      exportError: null,
      popupBlocked: false,
      exportUrl: null,
      availableOptions: [
        "content",
        "feedback",
        "errors",
        "comments",
        "hideGrades",
      ],
      description: "",
    };
  },

  computed: {
    multipleEssaysSelected() {
      return this.selectedIds.length > 1;
    },
  },

  mounted() {
    this.setDefaultOptions();
    if (!this.description) {
      this.description = this.$t("essays.export.selectData");
    }
  },

  methods: {
    async exportTo(payload) {
      return exportToPdf(payload);
    },

    async onExport() {
      if (!this.selectedAll && this.selectedIds.length === 0) {
        this.$showToastError(this.$t("essays.cannotExportFailed"));
        return;
      }

      if (this.exporting) return;

      this.exporting = true;
      this.success = false;

      try {
        if (this.type === "csv") {
          // CSV export - direct blob download without polling
          const { data, headers } = await this.exportTo({
            essaysIds: this.selectedIds,
            fields: this.selected,
            filters: this.filters,
            doNotArchive: this.mergedPdf,
          });
          const contentDisposition = headers["content-disposition"];
          this.fileName =
            contentDisposition?.match(/filename="(.+)"/)?.[1] ||
            "exported-essays.csv";

          this.exportUrl = URL.createObjectURL(data);
          await this.handleExportReady({ url: this.exportUrl });
        } else {
          // PDF export - async with polling
          const jobResponse = await startPdfExport({
            essaysIds: this.selectedIds,
            fields: this.selected,
            doNotArchive: this.mergedPdf,
          });
          const contentDisposition = jobResponse.headers["content-disposition"];
          this.fileName =
            contentDisposition?.match(/filename="(.+)"/)?.[1] ||
            "exported-essay.pdf";

          this.initPolling(jobResponse.data.job_id || jobResponse.data.jobId);
        }
      } catch (error) {
        console.error("Export failed:", error);
        this.exporting = false;
        this.$showToastError(
          error?.response?.data?.error || this.$t("errors.pdfExportFailed")
        );
      }
    },

    initPolling(jobId) {
      this.polling = createPolling({
        callback: this.checkExportStatus,
        interval: 2000,
        maxRetries: 30,
        onError: this.handlePollingError,
      });
      this.polling.currentJobId = jobId;
      this.polling.start();
    },

    async checkExportStatus() {
      try {
        const jobStatus = await checkPdfExportStatus(this.polling.currentJobId);
        const { data } = jobStatus;
        if (data.status === "ready") {
          await this.handleExportReady(data);
          return data;
        } else if (data.status === "pending") {
          return data;
        } else if (data.status === "error") {
          this.polling?.stop();
          this.handlePollingError({
            message: data.message || this.$t("errors.pdfExportFailed"),
          });
        }
      } catch (error) {
        this.polling?.stop();
        throw error;
      }
    },

    async handleExportReady(data) {
      this.exportUrl = data.url;

      if (this.type === "csv") {
        const link = document.createElement("a");
        link.href = data.url;
        link.download = this.fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.popupBlocked = false;
      } else {
        const newWindow = window.open(
          data.url,
          "_blank",
          "noopener,noreferrer"
        );

        // Check if popup was blocked
        this.popupBlocked =
          !newWindow ||
          newWindow.closed ||
          typeof newWindow.closed === "undefined";
      }

      this.exporting = false;
      this.success = true;
      if (this.polling?.isPolling) {
        this.polling.stop();
      }
    },

    handlePollingError(error) {
      console.error("Polling error:", error);
      this.exporting = false;
      this.exportError = error.message || this.$t("errors.pdfExportFailed");
      this.$showToastError(this.exportError);
    },

    setDefaultOptions() {
      this.selected = ["content", "feedback", "errors", "comments"];
    },

    async onShow() {
      this.show = true;
    },

    onHide() {
      this.polling?.stop();
      this.show = false;
      this.success = false;
      this.exporting = false;
      this.popupBlocked = false;

      // Clean up blob URL to prevent memory leaks
      if (this.exportUrl && this.exportUrl.startsWith("blob:")) {
        URL.revokeObjectURL(this.exportUrl);
      }
      this.exportUrl = null;

      this.setDefaultOptions();
      this.$emit("hide");
    },
  },
};
</script>
